import { QueryState, stopPolling } from '@redux-requests/core';
import { Action, createAction, Store } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { Report, ReportStatuses } from 'types';
import { getCommentedDifferences, getFilteredDifferences, getSegmentCommentedDifferenceCounts, app } from 'store';
import { trackEvent } from '../../components/Tracker/TrackerProvider';
import reports from './reducer';
import { ReportStates, useReportState } from 'zstore/reportStore';

// Single-mode dialogs reopen on `Idle`, so leaking `Idle` from a failed
// multi-inspection flow would auto-open the modal next time the user lands
// on a Results page. Multi reports own their dialog lifecycle in SessionList,
// so on failure we reset the shared store to `Closed` instead.
const handleReportRequestError = () => {
  const store = useReportState.getState();
  if (store.isMultiInspection) {
    store.setIsMultiInspection(false);
    store.setReportState(ReportStates.Closed);
    return;
  }
  store.setReportState(ReportStates.Idle);
};

const fetchReportById = createAction('request/fetchReportById', (reportId: string) => ({
  payload: {
    request: {
      url: `/reports/${reportId}`,
      method: 'GET',
    },
  },
  meta: {
    errorMessage: 'There was an error, please try again.',
    onSuccess: async (response: QueryState<Report>, _requestAction: Action, store: Store) => {
      if (response.data.status !== ReportStatuses.completed) {
        // this shouldn't happen because we are polling the report status until it's completed
        store.dispatch(app.actions.setSnackMessage({ message: 'Unable to download your report.', type: 'error' }));
        handleReportRequestError();
        return response;
      }

      useReportState.getState().setLastReportId(reportId);
      useReportState.getState().setReportPath(response.data.signedUrl);
      useReportState.getState().setReportState(ReportStates.Generated);
      return response;
    },
    onError: (error: AxiosError, _requestAction: Action, store: Store) => {
      store.dispatch(
        app.actions.setSnackMessage({ message: 'Something went wrong generating your report.', type: 'error' }),
      );
      handleReportRequestError();
      return error;
    },
  },
}));

const pollReportById = createAction('request/pollReportById', (reportId: string, inspectionId: string) => ({
  payload: {
    request: {
      url: `/reports/${reportId}/status`,
      method: 'GET',
    },
  },
  meta: {
    poll: 10,
    onSuccess: (response: QueryState<ReportStatuses>, _requestAction: Action, store: Store) => {
      if (response.data === ReportStatuses.error) {
        store.dispatch(stopPolling([pollReportById.toString()]));
        store.dispatch(app.actions.setSnackMessage({ message: 'Unable to generate report', type: 'error' }));
        handleReportRequestError();
      } else if (response.data === ReportStatuses.completed) {
        const state = store.getState();
        const commentedDifferencesQty = getCommentedDifferences(state).length;
        const commentedDifferencesType = getSegmentCommentedDifferenceCounts(state);
        const filteredDifferencesQty = getFilteredDifferences(state).length;
        trackEvent({
          name: 'report-generated',
          data: {
            commentedDifferencesQty,
            commentedDifferencesType,
            filteredDifferencesQty,
          },
        });
        store.dispatch(stopPolling([pollReportById.toString()]));
        store.dispatch(fetchReportById(reportId));
      }

      return response;
    },
    onError: (error: AxiosError, _requestAction: Action, store: Store) => {
      store.dispatch(stopPolling([pollReportById.toString()]));
      handleReportRequestError();
      return error;
    },
    errorMessage: 'Unable to generate report',
  },
}));

export type CreateReportPayload = {
  annotatedReport?: {
    source: boolean;
    new: boolean;
    keepExistingAnnotations?: boolean;
    showTextHighlights?: boolean;
  };

  summaryReport?: {
    excludedResults?: string;
    showTextHighlights?: boolean;
  };

  detailedReport?: {
    includeThumbnails: boolean;
    excludedResults?: string;
    showTextHighlights?: boolean;
  };

  xfdfReport?: boolean;

  ctfVersion?: string;

  includeLinkedInspections?: boolean;

  linkedInspectionIds?: string[];

  uploadGroundTruth?: boolean;
};

export type CreateMultiInspectionReportPayload = {
  inspectionIds: string[];
  annotatedReport?: {
    source: boolean;
    new: boolean;
    keepExistingAnnotations?: boolean;
    showTextHighlights?: boolean;
  };

  summaryReport?: {
    showTextHighlights?: boolean;
  };

  detailedReport?: {
    includeThumbnails: boolean;
    showTextHighlights?: boolean;
  };

  ctfVersion?: string;

  uploadGroundTruth?: boolean;
};

const createReport = createAction('request/createReport', (inspectionId: string, data: CreateReportPayload) => ({
  payload: {
    request: {
      url: `/reports/${inspectionId}`,
      method: 'POST',
      data,
    },
  },
  meta: {
    errorMessage: 'There was an error, please try again.',
    onRequest: (request: Request, _requestAction: Action, store: Store) => {
      const state = store.getState();
      const { report } = state.reports;

      store.dispatch(
        reports.actions.setReport({ ...report, path: '', signedUrl: '', status: ReportStatuses.inprogress }),
      );

      trackEvent({
        name: 'report-started',
      });

      return request;
    },
    onSuccess: (response: QueryState<Report>, _requestAction: Action, store: Store) => {
      store.dispatch(pollReportById(response.data.id, inspectionId));
      return response;
    },
    onError: (error: AxiosError, _requestAction: Action, store: Store) => {
      store.dispatch(app.actions.setSnackMessage({ message: 'Unable to generate report', type: 'error' }));
      handleReportRequestError();
    },
  },
}));

const uploadReport = createAction(
  'request/uploadReport',
  (inspectionId?: string, integration?: string, fileName?: string, reportId?: string, email?: string) => ({
    payload: {
      request: {
        url: `reports/${reportId}/upload`,
        method: 'POST',
        data: {
          integration,
          fileName,
          inspectionId,
          email,
        },
      },
    },
    meta: {
      errorMessage: 'Unable to upload report',
      successMessage: 'Report successfully uploaded!',
    },
  }),
);

const fetchReports = createAction('request/fetchReports', (inspectionId: string) => ({
  payload: {
    request: {
      url: `reports/inspection/${inspectionId}`,
      method: 'GET',
    },
  },
}));

const createMultiInspectionReport = createAction(
  'request/createMultiInspectionReport',
  (data: CreateMultiInspectionReportPayload) => ({
    payload: {
      request: {
        url: `/reports/multi-inspection`,
        method: 'POST',
        data,
      },
    },
    meta: {
      errorMessage: 'There was an error generating the multi-inspection report. Please try again.',
      onRequest: (request: Request, _requestAction: Action, store: Store) => {
        const state = store.getState();
        const { report } = state.reports;

        store.dispatch(
          reports.actions.setReport({ ...report, path: '', signedUrl: '', status: ReportStatuses.inprogress }),
        );

        trackEvent({
          name: 'multi-inspection-report-started',
          data: {
            inspectionCount: data.inspectionIds.length,
          },
        });

        return request;
      },
      onSuccess: (response: QueryState<Report>, _requestAction: Action, store: Store) => {
        // Poll the report status - reuse existing pollReportById with first inspection ID
        store.dispatch(pollReportById(response.data.id, data.inspectionIds[0]));
        return response;
      },
      onError: (error: AxiosError, _requestAction: Action, store: Store) => {
        store.dispatch(
          app.actions.setSnackMessage({ message: 'Unable to generate multi-inspection report', type: 'error' }),
        );
        handleReportRequestError();
      },
    },
  }),
);

export { fetchReportById, createReport, uploadReport, fetchReports, pollReportById, createMultiInspectionReport };
